<template>
  <div class="adminhome" style="min-height:85vh; position:relative;">
    <div style="display:flex; flex-wrap:wrap; gap:12px; margin-bottom:16px;">
      <div style="margin: 16px 0; display:flex; align-items:center; gap:16px;">
        <a-breadcrumb>
          <a-breadcrumb-item style="font-size:30px;">Country FAQ</a-breadcrumb-item>
        </a-breadcrumb>
        <a-button type="primary">
          <router-link to="/admin/country-faq/add">Add FAQ</router-link>
        </a-button>
      </div>
      <div style="margin-left:auto; display:flex; gap:12px; flex-wrap:wrap;">
        <a-select
          allow-clear
          style="min-width:180px;"
          placeholder="Filter by country"
          v-model="countryFilter"
          @change="handleFilterChange"
        >
          <a-select-option v-for="country in countries" :key="country._id" :value="country._id">
            {{ country.country }}
          </a-select-option>
        </a-select>
        <a-select
          allow-clear
          style="min-width:150px;"
          placeholder="Status"
          v-model="statusFilter"
          @change="handleFilterChange"
        >
          <a-select-option :value="1">Published</a-select-option>
          <a-select-option :value="0">Draft</a-select-option>
        </a-select>
        <a-input-search
          placeholder="Search question"
          style="max-width:320px;"
          v-model="searchText"
          @search="handleSearch"
          allow-clear
        />
      </div>
    </div>

    <a-card>
      <a-table
        :loading="loading"
        :data-source="decoratedFaqs"
        :row-key="record => record._id"
        :pagination="tablePagination"
        @change="handleTableChange"
        :scroll="{ x: 900 }"
      >
        <a-table-column key="country" title="Country" data-index="countryName" />
        <a-table-column key="question" title="Question" data-index="question" />
        <a-table-column key="status" title="Status">
          <template slot-scope="text, record">
            <a-tag :color="record.status === 1 ? 'green' : 'orange'">
              {{ record.status === 1 ? 'Published' : 'Draft' }}
            </a-tag>
          </template>
        </a-table-column>
        <a-table-column key="updatedAt" title="Updated" data-index="updatedAtFormatted" />
        <a-table-column key="actions" title="Actions" :width="200">
          <template slot-scope="text, record">
            <a-button type="link" @click="goToEdit(record)">Edit</a-button>
            <a-divider type="vertical" />
            <a-popconfirm
              title="Are you sure you want to delete this FAQ?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="handleDelete(record)"
            >
              <a-button type="link" :loading="deletingId === record._id">Delete</a-button>
            </a-popconfirm>
          </template>
        </a-table-column>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import moment from "moment";
import { api } from "../helpers/Helpers";

export default {
  data() {
    return {
      loading: false,
      deletingId: null,
      faqList: [],
      countries: [],
      countryFilter: undefined,
      statusFilter: undefined,
      searchText: "",
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    countryMap() {
      return this.countries.reduce((acc, cur) => {
        acc[cur._id] = cur;
        return acc;
      }, {});
    },
    decoratedFaqs() {
      return this.faqList.map((item) => {
        const countryInfo = this.countryMap[item.country_id] || {};
        return {
          ...item,
          countryName: countryInfo.country || "—",
          updatedAtFormatted: item.updatedAt
            ? moment(item.updatedAt).format("MMM DD, YYYY")
            : "—",
        };
      });
    },
    tablePagination() {
      return Object.assign({}, this.pagination, {
        showSizeChanger: false,
      });
    },
  },
  created() {
    this.initData();
  },
  methods: {
    async initData() {
      await Promise.all([this.fetchCountries(), this.fetchFaqs()]);
    },
    async fetchCountries() {
      try {
        const res = await api.getCountryInfo();
        this.countries = Array.isArray(res)
          ? res.sort((a, b) => (a.country || "").localeCompare(b.country || ""))
          : [];
      } catch (error) {
        console.error(error);
        this.$message.error("Failed to load countries.");
      }
    },
    buildQueryParams() {
      const params = {
        page: this.pagination.current,
        limit: this.pagination.pageSize,
      };
      if (this.countryFilter) {
        params.countryId = this.countryFilter;
      }
      if (this.statusFilter !== undefined && this.statusFilter !== null && this.statusFilter !== "") {
        params.status = this.statusFilter;
      }
      if (this.searchText && this.searchText.trim().length > 0) {
        params.search = this.searchText.trim();
      }
      return params;
    },
    async fetchFaqs() {
      this.loading = true;
      try {
        const res = await api.getCountryFaqs({ params: this.buildQueryParams() });
        const faqs = res && Array.isArray(res.faqs) ? res.faqs : [];
        this.faqList = faqs;
        const total = typeof (res && res.total) === "number" ? res.total : faqs.length;
        this.pagination = Object.assign({}, this.pagination, {
          total,
          current: (res && res.page) || this.pagination.current,
          pageSize: (res && res.limit) || this.pagination.pageSize,
        });
      } catch (error) {
        console.error(error);
        this.$message.error("Failed to load FAQs.");
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.pagination.current = 1;
      this.fetchFaqs();
    },
    handleFilterChange() {
      this.pagination.current = 1;
      this.fetchFaqs();
    },
    handleTableChange(pagination) {
      this.pagination = {
        ...this.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      };
      this.fetchFaqs();
    },
    goToEdit(record) {
      this.$router.push({ path: "/admin/country-faq/edit", query: { id: record._id } });
    },
    handleDelete(record) {
      return async () => {
        if (this.deletingId) return;
        this.deletingId = record._id;
        try {
          await api.deleteCountryFaq(record._id);
          this.$message.success("FAQ deleted.");
          this.pagination.current = 1;
          await this.fetchFaqs();
        } catch (error) {
          console.error(error);
          this.$message.error("Failed to delete FAQ.");
        } finally {
          this.deletingId = null;
        }
      };
    },
  },
};
</script>
